/**
 * @fileOverview The suppliers walkthrough: the overview, the setup, the Vergleich, and
 * one supplier's page with its Konditionen and Sortiment. Pure data.
 * See docs/view-help.md and docs/view-help/suppliers.md.
 */

import { ENTITY_ICONS } from "@/services/entityIcons"
import { PERMISSIONS } from "@/services/permission"
import { hasPermissionsOfAll, hasPermissionsOfAtLeastOne } from "@/services/permissionService"
import { insightKinds } from "@/services/supplierInsights"
import { SUPPLIER_SETUP_PERMISSIONS, isSupplierStrict } from "@/services/supplierMode"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpAction, ViewHelpTour } from "@/services/viewHelp/types"

/** Reading suppliers — every view of the feature needs it (`router/routes/supplier.js`). */
const VIEW = [PERMISSIONS.VIEW_SUPPLIER_GENERAL]

/** Entering or changing one — the create button and Speichern. */
const EDIT = [PERMISSIONS.EDIT_SUPPLIER_GENERAL]

/** Who reads a supplier's Konditionen: a reader of either catalogue's purchase prices (`canViewSupplierConditions`). */
const VIEW_PRICES = [PERMISSIONS.VIEW_MATERIAL_PURCHASEPRICE, PERMISSIONS.VIEW_MACHINE_PURCHASEPRICE]

/**
 * Who may change the Konditionen (`canEditSupplierConditions`): the supplier's own right and either catalogue's purchase
 * price right. Only they get the link to Miete and Transport.
 */
const editsConditions = () =>
  hasPermissionsOfAll(EDIT).value &&
  hasPermissionsOfAtLeastOne([PERMISSIONS.EDIT_MATERIAL_PURCHASEPRICE, PERMISSIONS.EDIT_MACHINE_PURCHASEPRICE]).value

/** The Sortiment shows a kind to whoever may read its records. */
const VIEW_ITEMS = [PERMISSIONS.VIEW_MATERIAL_GENERAL, PERMISSIONS.VIEW_MACHINE_GENERAL]

/** Krämer's Sortiment is materials, and its buttons follow that kind's price right. */
const EDIT_MATERIAL_PRICES = [PERMISSIONS.EDIT_MATERIAL_PURCHASEPRICE]

/** Who may move bookings that follow a supplier onto their day's price — what the hint asks first. */
const REPRICE = [
  PERMISSIONS.EDIT_ASSIGNED_PROJECT_MATERIAL_PURCHASEPRICE,
  PERMISSIONS.EDIT_FOREIGN_PROJECT_MATERIAL_PURCHASEPRICE,
  PERMISSIONS.EDIT_ASSIGNED_PROJECT_MACHINE_PURCHASEPRICE,
  PERMISSIONS.EDIT_FOREIGN_PROJECT_MACHINE_PURCHASEPRICE,
]

/**
 * The two states of a workspace (docs/suppliers.md, "Setup and strict mode"). Before the setup there is the invitation
 * and the setup page; after it the Sortiment, the switch and everything that reads supplier prices. A step for
 * the other state would only cost a skipped step's wait.
 */
const strict = () => isSupplierStrict()
const notSetUp = () => !isSupplierStrict()
const mayRunSetup = () => notSetUp() && hasPermissionsOfAll(SUPPLIER_SETUP_PERMISSIONS).value
/** "Preise ändern" without the add-on is a plain button, with it a menu that also imports a price list. */
/** The add-on's Vergleich is open where the drawer's gate for it is. */
const INSIGHTS = "nav-supplier-insights"

/** The sample supplier every step of its page is read on — the world's Baustoffe Krämer. */
const SUPPLIER = { supplierId: "sup-kraemer" }

/** The Sortiment row whose Verlauf is opened: Krämer's Bewehrungsstahl, three prices in a year. */
const STEEL = "pr-stahl-kraemer"

const onOverview = stepsOn("SupplierOverview", { permissions: VIEW })
const onSetup = stepsOn("SupplierSetup", { when: notSetUp })
const onComparison = stepsOn("SupplierComparison", { navEntry: INSIGHTS })
// The Vergleich of Huber and Krämer is of materials: it needs a reader who may see materials and their prices — the
// view compares only such a kind, and machines alone have nothing in common there.
const comparesMaterials = () => insightKinds().includes("MATERIAL")
const onDetail = stepsOn("SupplierDetail", { permissions: VIEW, at: { params: SUPPLIER } })

// The related views: where a supplier's prices take effect outside its own pages — the material that lists
// it among its suppliers, and the project that books its price of the day. The pointer walks there
// (`services/viewHelp/ways.ts`); `at` says where to load the frame if that walk ever fails.
const onMaterial = stepsOn("MaterialDetail", {
  permissions: [PERMISSIONS.VIEW_MATERIAL_GENERAL],
  when: strict,
  at: { params: { materialId: "mat-beton" }, hash: "purchase" },
})
const onProject = stepsOn("ProjectDetail", {
  permissions: [
    PERMISSIONS.VIEW_ASSIGNED_PROJECT_MATERIAL_PURCHASEPRICE,
    PERMISSIONS.VIEW_FOREIGN_PROJECT_MATERIAL_PURCHASEPRICE,
  ],
  when: strict,
  at: { params: { projectId: "p-nord" }, hash: "materials" },
})

const openTab = (target: string): ViewHelpAction => ({ do: "openTab", target })

/**
 * Suppliers: where machines are rented and materials bought, and — once the workspace's setup is done —
 * where every purchase price lives.
 *
 * The overview first, with the invitation to the setup or, after it, the switch to the Vergleich; then the
 * setup page (before it) and the Vergleich (with the add-on); then
 * Baustoffe Krämer's page register by register — Konditionen (with the link to the rental ones, Miete and Transport),
 * Sortiment with "Preise ändern" and a row's Verlauf, Ansprechpartner, Standorte, Dateiablage, Protokoll. The Zusammenhänge are the two places a supplier's
 * price arrives: a material's Einkauf and a project's row, with its Objektpreise.
 *
 * Nothing is saved. The pointer presses tabs, types a quantity into the Vergleich and opens its side by side, and
 * asks the setup for its Vorschau, which only reads.
 */
export const SUPPLIERS_TOUR: ViewHelpTour = {
  key: "suppliers",
  icon: ENTITY_ICONS.supplier,
  // The create form has no steps of its own; its trigger opens the tour at the top of the list.
  routes: ["SupplierOverview", "SupplierSetup", "SupplierComparison", "SupplierDetail", "SupplierCreate"],
  related: ["MaterialDetail", "ProjectDetail"],
  scenes: [
    // One name of the setup's list given to an existing supplier, then the Vorschau asked for. Neither needs
    // undoing: nothing is applied, and the next view is another page.
    { key: "setup", enter: [], exit: [] },
    // The registers, one press each. Nothing to put back: the tab that is open is where the next starts.
    { key: "general", enter: [], exit: [] },
    { key: "assortment", enter: [], exit: [] },
    // A row's Verlauf, opened from its menu and closed with Escape.
    { key: "revisions", enter: [openTab("supplier-tab-assortment")], exit: [{ do: "escape" }] },
    { key: "contacts", enter: [], exit: [] },
    { key: "locations", enter: [], exit: [] },
    { key: "files", enter: [], exit: [] },
    { key: "activity", enter: [], exit: [] },
    // The material's Einkauf, pressed by its step.
    { key: "material-purchase", enter: [], exit: [], endsWithView: true },
    // The project's Beton row, unfolded to its purchase prices and folded again by the same arrow.
    {
      key: "project-prices",
      enter: [openTab("project-tab-materials")],
      exit: [{ do: "collapse", target: "project-material-row-mat-beton" }],
    },
  ],
  steps: [
    // ── Übersicht ────────────────────────────────────────────────────────────
    onOverview("list", "supplier-table", ENTITY_ICONS.supplier, { placement: "top" }),
    onOverview("row", "row-sup-kraemer", "mdi-table-row", { placement: "bottom" }),
    // Pointed at, not pressed: the create form is not part of this walkthrough.
    onOverview("create", "supplier-create", "mdi-plus", { placement: "bottom", permissions: EDIT }),
    // Before the setup: the one way to it.
    onOverview("invitation", "supplier-setup-invitation", "mdi-auto-fix", { placement: "bottom", when: mayRunSetup }),
    onOverview("views", "supplier-views", "mdi-view-split-vertical", { placement: "bottom", when: strict }),

    // ── Lieferanten einrichten (before the setup) ────────────────────────────
    onSetup("setup-table", "supplier-setup-table", "mdi-format-list-text", { placement: "top" }),
    // "Krämer" was the office's name for Baustoffe Krämer: the pointer gives it the existing supplier.
    onSetup("setup-target", "supplier-setup-target-krämer", "mdi-swap-horizontal", {
      placement: "bottom",
      scene: "setup",
      actions: [
        { do: "click", target: "supplier-setup-target-krämer" },
        { do: "choose", text: "Baustoffe Krämer" },
      ],
    }),
    // The Vorschau only reads (the sandbox answers it); Übernehmen is pointed at and never pressed.
    onSetup("setup-preview", "supplier-setup-preview", "mdi-eye-outline", {
      placement: "top",
      scene: "setup",
      actions: [{ do: "click", target: "supplier-setup-preview-action" }],
    }),
    onSetup("setup-apply", "supplier-setup-apply", "mdi-check-all", { placement: "top" }),

    // ── Vergleich (add-on) ───────────────────────────────────────────────────
    onComparison("comparison", "supplier-view-SupplierComparison", "mdi-compare-horizontal", { placement: "bottom" }),
    // Huber and Krämer, the two sharing the most materials, a card each, nothing to pick first.
    onComparison("comparison-cards", "supplier-comparison-cards", "mdi-compare-horizontal", {
      placement: "top",
      when: comparesMaterials,
    }),
    onComparison("comparison-pick", "supplier-comparison-card-0", ENTITY_ICONS.supplier, {
      placement: "bottom",
      when: comparesMaterials,
    }),
    onComparison("comparison-only", "supplier-comparison-only-0", "mdi-chevron-right", {
      placement: "top",
      when: comparesMaterials,
    }),
    // Pointed at, not pressed: a card without its supplier would stand in every later step.
    onComparison("comparison-add", "supplier-comparison-add", "mdi-plus", {
      placement: "left",
      when: comparesMaterials,
    }),
    // The first row; the dialog a click on it opens only reads.
    onComparison("comparison-history", "supplier-comparison-row", "mdi-chart-line", {
      placement: "bottom",
      when: comparesMaterials,
    }),

    // ── Baustoffe Krämer ─────────────────────────────────────────────────────
    // The card's head is `LewEntityPage`'s, not this view's, which is why it carries its own anchor.
    onDetail("head", "entity-head", ENTITY_ICONS.supplier, { placement: "bottom" }),
    onDetail("tabs", "supplier-tabs", "mdi-tab", { placement: "bottom" }),
    // The register is pressed with the whole page in shot: zoomed onto the tab, the panel that slides in
    // underneath would be out of the picture. The groups after it are inside that panel.
    onDetail("base", "supplier-base", "mdi-form-textbox", {
      placement: "bottom",
      scene: "general",
      zoom: false,
      actions: [openTab("supplier-tab-general")],
    }),
    onDetail("reach", "supplier-reach", "mdi-phone-outline", { placement: "bottom", scene: "general" }),
    onDetail("address", "supplier-address", "mdi-map-marker-outline", { placement: "top", scene: "general" }),
    // The Konditionen are on the page before the setup too, but act only on supplier prices, which is what
    // the two steps say.
    onDetail("discount", "supplier-conditions-discount", "mdi-percent-outline", {
      placement: "top",
      scene: "general",
      when: strict,
      permissions: VIEW_PRICES,
    }),
    onDetail("terms", ["supplier-conditions-cash", "supplier-conditions-freight"], "mdi-truck-outline", {
      placement: "top",
      scene: "general",
      when: strict,
      permissions: VIEW_PRICES,
    }),
    // A rental supplier's two, Miete and Transport, show where it has machines in its Sortiment. Krämer has none, so
    // its page offers the link that opens them, pointed at, not pressed; the step says where they show. Not on Mietpark
    // Nord's page: the player hops only when the route changes, and that page is the same route as Krämer's.
    onDetail("transport", "supplier-conditions-rental-open", "mdi-tow-truck", {
      placement: "top",
      scene: "general",
      when: () => strict() && editsConditions(),
    }),
    onDetail("assortment", "supplier-assortment", "mdi-format-list-bulleted", {
      placement: "bottom",
      scene: "assortment",
      zoom: false,
      when: strict,
      permissions: VIEW_ITEMS,
      actions: [openTab("supplier-tab-assortment")],
    }),
    onDetail("assortment-rows", "supplier-assortment-table", "mdi-table", {
      placement: "top",
      scene: "assortment",
      when: strict,
      permissions: VIEW_ITEMS,
    }),
    onDetail("following", "following-bookings-hint", "mdi-calendar-sync-outline", {
      placement: "top",
      scene: "assortment",
      when: strict,
      permissions: REPRICE,
    }),
    // "Preise ändern" opens the price list: pointed at, not pressed (it writes the Sortiment).
    onDetail("price-list", "supplier-assortment-price-list", "mdi-pencil-outline", {
      placement: "bottom",
      scene: "assortment",
      when: strict,
      permissions: EDIT_MATERIAL_PRICES,
    }),
    // The add-on's import, in the ⋮ beside it: the menu pointed at, not opened (its entry asks for a file).
    onDetail("price-import", "supplier-assortment-menu", "mdi-file-upload-outline", {
      placement: "bottom",
      scene: "assortment",
      when: strict,
      navEntry: INSIGHTS,
      permissions: EDIT_MATERIAL_PRICES,
    }),
    // A row's menu, its Verlauf pressed: the dated prices of Krämer's Bewehrungsstahl. The pause is for the
    // menu, whose entries are in the page before it has faded in.
    onDetail("revisions", "supplier-revisions", "mdi-history", {
      placement: "left",
      scene: "revisions",
      when: strict,
      permissions: VIEW_ITEMS,
      actions: [
        { do: "click", target: `supplier-assortment-row-${STEEL}` },
        { do: "wait", ms: 400 },
        { do: "click", target: `supplier-assortment-row-${STEEL}-history` },
      ],
    }),
    onDetail("contacts", "supplier-contacts", "mdi-account-multiple-outline", {
      placement: "bottom",
      scene: "contacts",
      zoom: false,
      actions: [openTab("supplier-tab-contacts")],
    }),
    // Krämer's depot in Waldbach, with its own Disposition.
    onDetail("locations", "supplier-locations", "mdi-map-marker-multiple-outline", {
      placement: "bottom",
      scene: "locations",
      zoom: false,
      actions: [openTab("supplier-tab-locations")],
    }),
    onDetail("files", "supplier-files", "mdi-folder-outline", {
      placement: "top",
      scene: "files",
      zoom: false,
      actions: [openTab("supplier-tab-files")],
    }),
    onDetail("activity", "supplier-activity", "mdi-history", {
      placement: "top",
      scene: "activity",
      zoom: false,
      actions: [openTab("supplier-tab-activity")],
    }),
    // Explained, never pressed: it writes the record.
    onDetail("save", "supplier-save", "mdi-content-save-outline", { placement: "top", permissions: EDIT }),

    // ── Where to find this again ─────────────────────────────────────────────
    onDetail("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── Zusammenhänge ──────────────────────────────────────────────────────────
    //
    // Where a supplier's prices arrive: among the suppliers of a material, and in a project, which books them and
    // may hold an Objektpreis of the supplier. One thought each, from the supplier's side — the materials and
    // projects tours say what their own screens do.
    // The supplier table, not the whole tab, brought on screen whole: under the chart it starts at the page's
    // lower edge, where the footer with Speichern covers its rows and the visibility test cannot tell.
    onMaterial("material-purchase", "material-purchase-table", ENTITY_ICONS.material, {
      placement: "top",
      scene: "material-purchase",
      actions: [openTab("material-tab-purchase"), { do: "scroll", target: "material-purchase-table", to: "reveal" }],
    }),
    onProject("project-prices", "project-purchase-price-variants", ENTITY_ICONS.project, {
      placement: "top",
      scene: "project-prices",
      actions: [{ do: "expand", target: "project-material-row-mat-beton" }],
    }),
  ],
}
